import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, TrendingUp, TrendingDown, Users, Building2, Briefcase, Wallet, Receipt } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const MetricCard = ({ title, value, icon: Icon, color = 'text-brand-primary', bgColor = 'bg-brand-primary/10', prefix = '$' }) => (
  <Card className="border border-brand-border bg-white metric-card">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center shrink-0`}>
          <Icon size={18} className={color} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-brand-text-secondary truncate">{title}</p>
          <p className="font-heading text-xl text-brand-text-primary">{prefix}{typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: 0 }) : value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function CompanyProfitPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = period ? { period } : {};
        const { data: d } = await api.get('/revenue/company-profit', { params });
        setData(d);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [period]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;

  const chartData = data ? [
    { name: 'Company Share', value: data.company_share, fill: '#163A2E' },
    { name: 'Trader Payouts', value: data.trader_payouts, fill: '#5B7B61' },
    { name: 'Manager Incentives', value: data.manager_incentives, fill: '#D99C6A' },
    { name: 'Salaries', value: data.salaries, fill: '#C25934' },
    { name: 'Expenses', value: data.expenses, fill: '#8CA496' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="company-profit-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Company P&L</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Financial overview: revenue, expenses, and net profit</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Period</Label>
          <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} placeholder="All time" className="bg-white border-brand-border rounded-sm w-44" data-testid="company-period" />
        </div>
      </div>

      {data && (
        <>
          {/* Net Profit Hero */}
          <Card className={`border-2 ${data.net_company_profit >= 0 ? 'border-brand-success/30 bg-brand-success/[0.02]' : 'border-brand-error/30 bg-brand-error/[0.02]'}`}>
            <CardContent className="p-6 text-center">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary mb-1">Net Company Profit</p>
              <p className={`font-heading text-4xl sm:text-5xl tracking-tight ${data.net_company_profit >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                ${data.net_company_profit.toLocaleString()}
              </p>
              <p className="text-xs text-brand-text-secondary mt-2">{data.period === 'all-time' ? 'All Time' : data.period}</p>
            </CardContent>
          </Card>

          {/* Revenue Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard title="Client Profit" value={data.total_client_profit} icon={TrendingUp} color="text-brand-success" bgColor="bg-brand-success/10" />
            <MetricCard title="Company Share" value={data.company_share} icon={Building2} color="text-brand-primary" bgColor="bg-brand-primary/10" />
            <MetricCard title="Total Outflow" value={data.total_outflow} icon={TrendingDown} color="text-brand-error" bgColor="bg-brand-error/10" />
            <MetricCard title="Net Profit" value={data.net_company_profit} icon={Wallet} color={data.net_company_profit >= 0 ? 'text-brand-success' : 'text-brand-error'} bgColor={data.net_company_profit >= 0 ? 'bg-brand-success/10' : 'bg-brand-error/10'} />
          </div>

          {/* Outflow Breakdown */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard title="Trader Payouts" value={data.trader_payouts} icon={Briefcase} color="text-[#5B7B61]" bgColor="bg-[#5B7B61]/10" />
            <MetricCard title="Manager Incentives" value={data.manager_incentives} icon={Users} color="text-[#D99C6A]" bgColor="bg-[#D99C6A]/10" />
            <MetricCard title="Salaries" value={data.salaries} icon={DollarSign} color="text-brand-secondary" bgColor="bg-brand-secondary/10" />
            <MetricCard title="Expenses" value={data.expenses} icon={Receipt} color="text-[#8CA496]" bgColor="bg-[#8CA496]/10" />
          </div>

          {/* Chart */}
          <Card className="border border-brand-border bg-white">
            <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary">Financial Breakdown</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              {chartData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#51635C' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#51635C' }} width={120} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {chartData.map((d) => <rect key={d.name} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-brand-text-secondary text-sm text-center py-8">No financial data to display</p>}
            </CardContent>
          </Card>

          {/* Ledger Summary */}
          <Card className="border border-brand-border bg-white">
            <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary">Financial Ledger</CardTitle></CardHeader>
            <CardContent className="space-y-0">
              {[
                { label: 'Total Client Profit (Trading)', value: data.total_client_profit, color: 'text-brand-success', sign: '+' },
                { label: 'Company Share (Revenue)', value: data.company_share, color: 'text-brand-primary', sign: '+' },
                { label: 'Trader Payouts', value: data.trader_payouts, color: 'text-brand-error', sign: '-' },
                { label: 'Manager Incentives', value: data.manager_incentives, color: 'text-brand-error', sign: '-' },
                { label: 'Salaries', value: data.salaries, color: 'text-brand-error', sign: '-' },
                { label: 'Operating Expenses', value: data.expenses, color: 'text-brand-error', sign: '-' },
              ].map((row) => (
                <div key={row.label} className="flex justify-between py-3 border-b border-brand-border/50">
                  <span className="text-sm text-brand-text-secondary">{row.label}</span>
                  <span className={`text-sm font-semibold ${row.color}`}>{row.sign}${row.value.toLocaleString()}</span>
                </div>
              ))}
              <div className="flex justify-between py-4">
                <span className="text-base font-bold text-brand-text-primary">Net Company Profit</span>
                <span className={`text-xl font-bold ${data.net_company_profit >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                  ${data.net_company_profit.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
